import { useMemo, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DOC_CATEGORIES, type EmployeeDocument } from '@dxtech/shared';
import { api } from '../api/client.js';
import { dateForOffset, useApp } from '../state/app.js';
import { Avatar, ConfirmDialog, Modal, Spinner } from '../ui/common.js';
import { ProfileModal } from '../modals/ProfileModal.js';
import { personalApi } from '../api/personal.js';

// 임직원 보드 = 고정 정보(입사일·생일·연락처·주소)만. 날마다 바뀌는 상태·일정은 근무현황 화면에서 봄
const GRID = '1.2fr 64px 70px 130px 140px 2fr 150px'; // 이름 · 혈액형 · MBTI · 입사일 · 생일 · 연락처/주소 · 관리

const p2 = (n: number): string => String(n).padStart(2, '0');

/** 올해 양력 생일('MM-DD') → { 'MM월 DD일 (요일)', 남은 일수 remain }. remain>=0=다가옴, <0=지남 */
function birthdayInfo(solar?: string): { date: string; remain: number } | null {
  if (!solar || solar.length < 5) return null;
  const mm = Number(solar.slice(0, 2));
  const dd = Number(solar.slice(3, 5));
  if (!mm || !dd) return null;
  const todayStr = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(new Date());
  const year = Number(todayStr.slice(0, 4));
  const bday = new Date(`${year}-${p2(mm)}-${p2(dd)}T00:00:00Z`);
  const today = new Date(`${todayStr}T00:00:00Z`);
  const remain = Math.round((bday.getTime() - today.getTime()) / 86400000);
  const wd = ['일', '월', '화', '수', '목', '금', '토'][bday.getUTCDay()];
  return { date: `${p2(mm)}월 ${p2(dd)}일 (${wd})`, remain };
}

/** 'YYYY-MM-DD' → 'YYYY.MM.DD' */
function ymdDot(d?: string): string {
  return d && d.length >= 10 ? d.slice(0, 10).replace(/-/g, '.') : '';
}

/** 입사일 이후 근속 일수(오늘 포함, KST). 미래/무효 시 null */
function daysWorked(hireDate?: string): number | null {
  if (!hireDate || hireDate.length < 10) return null;
  const today = new Date(`${new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(new Date())}T00:00:00Z`);
  const hire = new Date(`${hireDate.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(hire.getTime())) return null;
  const diff = Math.floor((today.getTime() - hire.getTime()) / 86400000) + 1; // 입사일 당일 = 1일
  return diff >= 1 ? diff : null;
}

export function Board(): JSX.Element {
  const { me } = useApp();
  const [docsTarget, setDocsTarget] = useState<string | null>(null);
  const [profileTarget, setProfileTarget] = useState<string | null>(null);

  const date = dateForOffset(0).date; // 고정 정보라 날짜 이동 없이 오늘 기준
  const board = useQuery({ queryKey: ['board', date], queryFn: () => api.board(date) });
  const entries = board.data?.entries ?? [];
  // 타 직원 프로파일/증명서 관리는 CEO/Admin(관리자)만 — 임원(Executive)은 본인 것만
  const isAdmin = me.permissions.canManageMembers;
  const people = useMemo(() => entries.map((e) => ({ upn: e.upn, name: e.name })), [entries]);
  // 혈액형·MBTI(개인 프로파일) — 이름 옆 별도 컬럼
  const personal = useQuery({ queryKey: ['personal'], queryFn: () => personalApi.list() });
  const personalMap = useMemo(() => new Map((personal.data?.items ?? []).map((p) => [p.upn.toLowerCase(), p])), [personal.data]);

  const filtered = entries;

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', animation: 'dxfade .3s ease' }}>
      <div className="dx-card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '16px 18px 13px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', borderBottom: '1px solid #F3F4F6' }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>임직원 보드</div>
          <span style={{ fontSize: 12, color: '#9CA3AF' }}>총 {entries.length}명 · 오늘의 상태·일정은 근무현황에서 확인하세요</span>
        </div>

        {board.isLoading ? (
          <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>표시할 임직원이 없습니다</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: 894 }}>
              <div style={{ display: 'grid', gridTemplateColumns: GRID, background: '#F9FAFB', borderBottom: '1px solid #EDEFF2', fontSize: 12, fontWeight: 600, color: '#6B7280' }}>
                <div style={{ padding: '10px 14px' }}>이름</div>
                <div style={{ padding: '10px 12px' }}>혈액형</div>
                <div style={{ padding: '10px 12px' }}>MBTI</div>
                <div style={{ padding: '10px 12px' }}>입사일</div>
                <div style={{ padding: '10px 12px' }}>생일</div>
                <div style={{ padding: '10px 12px' }}>연락처 / 주소</div>
                <div style={{ padding: '10px 12px', textAlign: 'center' }}>관리</div>
              </div>
              {filtered.map((e, i) => (
                <div key={e.upn} style={{ display: 'grid', gridTemplateColumns: GRID, borderBottom: '1px solid #F6F7F9', fontSize: 13, alignItems: 'center', background: i % 2 === 0 ? '#fff' : '#FAFBFC' }}>
                  <div style={{ padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
                    <Avatar name={e.name} index={i} size={30} upn={e.upn} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, ...ellipsis }}>{e.name}</div>
                      <div style={{ fontSize: 12, color: '#9CA3AF' }}>{e.dept}</div>
                    </div>
                  </div>
                  {(() => {
                    const p = personalMap.get(e.upn.toLowerCase());
                    return (
                      <>
                        <div style={{ padding: '11px 12px', whiteSpace: 'nowrap', color: p?.bloodType ? '#4B5563' : '#D1D5DB', fontWeight: 600 }}>{p?.bloodType ? `${p.bloodType}형` : '—'}</div>
                        <div style={{ padding: '11px 12px', whiteSpace: 'nowrap', color: p?.mbti ? '#2563EB' : '#D1D5DB', fontWeight: 600 }}>{p?.mbti || '—'}</div>
                      </>
                    );
                  })()}
                  <div style={{ padding: '11px 12px', whiteSpace: 'nowrap' }}>
                    {e.hireDate ? (
                      <>
                        <div className="tnum" style={{ color: '#4B5563', fontWeight: 600 }}>{ymdDot(e.hireDate)}</div>
                        {daysWorked(e.hireDate) != null && (
                          <div className="tnum" style={{ fontSize: 11, color: '#9CA3AF', marginTop: 1 }}>{daysWorked(e.hireDate)!.toLocaleString()}일 근무</div>
                        )}
                      </>
                    ) : <span style={{ color: '#D1D5DB' }}>—</span>}
                  </div>
                  <div style={{ padding: '11px 12px' }}>
                    {(() => {
                      const b = birthdayInfo(e.birthdaySolar);
                      if (!b) return <span style={{ color: '#D1D5DB' }}>—</span>;
                      return (
                        <>
                          <div style={{ color: '#4B5563', fontWeight: 600, whiteSpace: 'nowrap', fontSize: 12.5 }}>{b.date}</div>
                          {b.remain >= 0 && (
                            <div style={{ fontSize: 11, marginTop: 1, fontWeight: 700, color: b.remain === 0 ? '#DC2626' : '#2563EB', whiteSpace: 'nowrap' }}>
                              {b.remain === 0 ? '오늘 🎂' : `${b.remain}일전`}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  {/* 연락처 / 주소 — 2줄 */}
                  <div style={{ padding: '11px 12px', minWidth: 0 }}>
                    <div className="tnum" style={{ color: e.phone ? '#4B5563' : '#D1D5DB', fontWeight: 600, ...ellipsis }}>{e.phone ?? '—'}</div>
                    <div style={{ fontSize: 12, color: e.address ? '#6B7280' : '#D1D5DB', marginTop: 2, ...ellipsis }} title={e.address ?? ''}>{e.address ?? '—'}</div>
                  </div>
                  {/* 관리 — 프로파일 / 증명서 (본인 또는 관리자) */}
                  <div style={{ padding: '9px 8px', display: 'flex', gap: 5, justifyContent: 'center', flexWrap: 'wrap' }}>
                    {(isAdmin || e.upn === me.upn) ? (
                      <>
                        <button className="dx-btn dx-btn-ghost" style={{ padding: '4px 8px', fontSize: 11.5 }} onClick={() => setProfileTarget(e.upn)}>프로파일</button>
                        <button className="dx-btn dx-btn-ghost" style={{ padding: '4px 8px', fontSize: 11.5 }} onClick={() => setDocsTarget(e.upn)}>증명서</button>
                      </>
                    ) : <span style={{ color: '#D1D5DB', fontSize: 12 }}>—</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {docsTarget && <DocsModal myUpn={me.upn} myName={me.name} isAdmin={isAdmin} people={people} initialTarget={docsTarget} onClose={() => setDocsTarget(null)} />}
      {profileTarget && <ProfileModal myUpn={me.upn} myName={me.name} isAdmin={isAdmin} people={people} initialTarget={profileTarget} onClose={() => setProfileTarget(null)} />}
    </div>
  );
}

const ellipsis = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } as const;

/** 'YYYY-MM-DD' or ISO → 'YYYY.MM.DD' */
function dDot(iso?: string): string {
  return iso && iso.length >= 10 ? iso.slice(0, 10).replace(/-/g, '.') : '';
}
function fmtSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/** 임직원 증명서 문서: 본인 업로드/삭제, 관리자는 직원 선택해 열람 */
function DocsModal({ myUpn, myName, isAdmin, people, initialTarget, onClose }: {
  myUpn: string; myName: string; isAdmin: boolean; people: { upn: string; name: string }[]; initialTarget?: string; onClose: () => void;
}): JSX.Element {
  const { flash } = useApp();
  const qc = useQueryClient();
  // 대상은 팝업을 연 임직원으로 고정(선택 불가)
  const target = initialTarget ?? myUpn;
  const [category, setCategory] = useState<string>(DOC_CATEGORIES[0]);
  const [busy, setBusy] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<EmployeeDocument | null>(null); // 삭제 확인 대상
  const isMine = target.toLowerCase() === myUpn.toLowerCase();
  const canManage = isMine || isAdmin; // 본인 또는 관리자: 업로드/삭제 가능
  const targetName = people.find((p) => p.upn.toLowerCase() === target.toLowerCase())?.name ?? myName;

  const docs = useQuery({ queryKey: ['docs', target], queryFn: () => api.docs(isMine ? undefined : target) });
  const items: EmployeeDocument[] = docs.data?.items ?? [];
  const refresh = () => qc.invalidateQueries({ queryKey: ['docs', target] });

  const del = useMutation({
    mutationFn: (id: string) => api.deleteDoc(id),
    onSuccess: () => { flash('문서를 삭제했습니다'); refresh(); },
    onError: (e) => flash(e instanceof Error ? e.message : '삭제 실패'),
  });

  const onPick = async (file: File): Promise<void> => {
    if (file.size > 6 * 1024 * 1024) { flash('파일은 6MB 이하만 업로드할 수 있습니다.'); return; }
    setBusy(true);
    try {
      const b64 = await new Promise<string>((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(String(fr.result).split(',')[1] ?? '');
        fr.onerror = () => reject(new Error('파일 읽기 실패'));
        fr.readAsDataURL(file);
      });
      await api.uploadDoc({ upn: isMine ? undefined : target, category, fileName: file.name, contentBase64: b64, contentType: file.type || 'application/octet-stream' });
      flash('업로드되었습니다');
      refresh();
    } catch (e) {
      flash(e instanceof Error ? e.message : '업로드 실패');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal width={520} onClose={onClose}>
      <div style={{ padding: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>증명서 · 문서함</div>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#374151', background: '#F1F5F9', border: '1px solid #E2E8F0', borderRadius: 999, padding: '3px 12px' }}>
            {targetName}{isMine ? ' · 본인' : ''}
          </span>
          <button title="닫기" onClick={onClose} style={{ marginLeft: 'auto', border: 'none', background: 'transparent', cursor: 'pointer', color: '#6B7280', fontSize: 27, lineHeight: 1 }}>×</button>
        </div>

        {/* 업로드 (본인 또는 관리자) */}
        {canManage ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: 12, background: '#F9FAFB', border: '1px solid #EDEFF2', borderRadius: 10, marginBottom: 14 }}>
            <select className="dx-select" value={category} onChange={(e) => setCategory(e.target.value)}>
              {DOC_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <label className="dx-btn dx-btn-primary" style={{ cursor: busy ? 'wait' : 'pointer', flex: 1, justifyContent: 'center', opacity: busy ? 0.6 : 1 }}>
              {busy ? '업로드 중…' : '업로드 파일 선택'}
              <input type="file" hidden disabled={busy}
                accept=".pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp,.hwp,.hwpx,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) void onPick(f); e.target.value = ''; }} />
            </label>
          </div>
        ) : (
          <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 12 }}>다운로드만 가능합니다.</div>
        )}

        {/* 목록 */}
        <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 6 }}>업로드된 문서 {items.length > 0 && `(${items.length})`}</div>
        <div style={{ border: '1px solid #EDEFF2', borderRadius: 10, overflow: 'hidden', maxHeight: 320, overflowY: 'auto' }}>
          {docs.isLoading ? (
            <div style={{ padding: 28, textAlign: 'center' }}><Spinner size={22} /></div>
          ) : items.length === 0 ? (
            <div style={{ padding: 26, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>업로드된 문서가 없습니다.</div>
          ) : items.map((d) => (
            <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderBottom: '1px solid #F6F7F9', fontSize: 13 }}>
              <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: '#EEF2FF', color: '#4338CA' }}>{d.category}</span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 600, ...ellipsis }}>{d.name}</div>
                <div style={{ fontSize: 11, color: '#9CA3AF' }}>{dDot(d.uploadedAt)} · {fmtSize(d.size)}</div>
              </div>
              <button className="dx-btn dx-btn-ghost" style={{ padding: '5px 10px', fontSize: 12, flexShrink: 0 }}
                onClick={() => { void api.downloadDoc(d.id, d.name).catch((e) => flash(e instanceof Error ? e.message : '다운로드 실패')); }}>다운로드</button>
              {/* 본인 파일이거나 관리자면 삭제 가능 (레이어드 확인 모달) */}
              {(d.ownerUpn?.toLowerCase() === myUpn.toLowerCase() || isAdmin) && (
                <button title="삭제" onClick={() => setPendingDelete(d)}
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#DC2626', fontSize: 16, lineHeight: 1, padding: '0 4px', flexShrink: 0 }}>×</button>
              )}
            </div>
          ))}
        </div>
        <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 10 }}>재직·경력증명서, 자격증, 통장사본 등 · 1개 파일 최대 6MB</div>
      </div>
      {pendingDelete && (
        <ConfirmDialog title="문서 삭제" message={`'${pendingDelete.name}' 문서를 삭제할까요?`} confirmLabel="삭제" danger busy={del.isPending}
          onConfirm={() => { del.mutate(pendingDelete.id); setPendingDelete(null); }}
          onCancel={() => setPendingDelete(null)} />
      )}
    </Modal>
  );
}
